import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { formatCurrency, formatPoints } from '../utils/formatters';
import { formatCurrencyInput, parseCurrencyInput } from '@/utils/currencyFormatting';
import { calculateBuydownScenarios, BuydownScenario, BuydownConfiguration, getDefaultConfiguration } from './utils/buydownCalculations';
import { TrendingDown, DollarSign, Calendar, Check, Settings, RotateCcw, ChevronDown } from 'lucide-react';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import LoanTypeSelector from './components/LoanTypeSelector';
import OccupancyTypeSelector from './components/OccupancyTypeSelector';
import RateSheetComparison from './RateSheetComparison';
import { AdvancedInvestorCalculator } from './components/AdvancedInvestorCalculator';
import { BreakevenAnalysisChart } from './BreakevenAnalysisChart';
import CoventryInsuranceSelector from '../inputs/CoventryInsuranceSelector';
import PITIDonutChart from './components/PITIDonutChart';

interface BuydownIncentiveCalculatorProps {
  homePrice: number;
  loanAmount: number;
  downPayment: number;
  loanType: 'conventional' | 'fha' | 'va' | 'usda';
  baseRate: number;
  monthlyTaxes: number;
  monthlyInsurance: number;
  monthlyHoa: number;
  monthlyPmi: number;
  totalClosingCosts: number;
  earnestMoneyDeposit?: number;
  onScenariosCalculated?: (scenarios: BuydownScenario[]) => void;
  onInsuranceChange?: (monthlyInsurance: number) => void;
}

const BuydownIncentiveCalculator: React.FC<BuydownIncentiveCalculatorProps> = ({
  homePrice,
  loanAmount,
  downPayment,
  loanType,
  baseRate,
  monthlyTaxes,
  monthlyInsurance,
  monthlyHoa,
  monthlyPmi,
  totalClosingCosts,
  earnestMoneyDeposit = 0,
  onScenariosCalculated,
  onInsuranceChange
}) => {
  const [builderName, setBuilderName] = useState('Coventry Homes');
  const [lenderName, setLenderName] = useState('Jet Home Loans');
  const [purchasePrice, setPurchasePrice] = useState(homePrice);
  const [displayPurchasePrice, setDisplayPurchasePrice] = useState(
    formatCurrencyInput(homePrice.toString())
  );
  
  // Coventry insurance lookup state
  const [selectedCommunity, setSelectedCommunity] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [customMonthlyInsurance, setCustomMonthlyInsurance] = useState(monthlyInsurance);
  
  // Loan type and occupancy state
  const [selectedLoanType, setSelectedLoanType] = useState<'conventional' | 'fha' | 'va' | 'usda'>(loanType);
  const [occupancyType, setOccupancyType] = useState<'primary' | 'investment' | 'second'>('primary');
  
  const [configuration, setConfiguration] = useState<BuydownConfiguration>(
    getDefaultConfiguration(selectedLoanType, baseRate, occupancyType)
  );

  // Sync display price when purchase price changes externally
  useEffect(() => {
    setDisplayPurchasePrice(formatCurrencyInput(purchasePrice.toString()));
  }, [purchasePrice]);

  // Update configuration when loan type or occupancy changes
  useEffect(() => {
    setConfiguration(getDefaultConfiguration(selectedLoanType, baseRate, occupancyType));
  }, [selectedLoanType, occupancyType, baseRate]);

  // Calculate current base points for display
  const currentBasePoints = useMemo(() => {
    const isConventional = selectedLoanType === 'conventional';
    let points = isConventional ? 5.994 : 4.063;
    
    if (isConventional && occupancyType !== 'primary') {
      points += 2.125;
    }
    
    return points.toFixed(3);
  }, [selectedLoanType, occupancyType]);

  const handleResetToDefaults = () => {
    setConfiguration(getDefaultConfiguration(selectedLoanType, baseRate, occupancyType));
  };

  const handleLoanTypeChange = (newLoanType: 'conventional' | 'fha' | 'va' | 'usda') => {
    setSelectedLoanType(newLoanType);
    // Reset occupancy to primary when switching away from conventional
    if (newLoanType !== 'conventional') {
      setOccupancyType('primary');
    }
  };

  const updateConfiguration = (updates: Partial<BuydownConfiguration>) => {
    setConfiguration(prev => ({ ...prev, ...updates }));
  };

  // Calculate all buydown scenarios
  const scenarios = useMemo(() => {
    return calculateBuydownScenarios({
      loanAmount,
      loanType: selectedLoanType,
      baseRate,
      purchasePrice,
      downPayment,
      monthlyTaxes,
      monthlyInsurance: customMonthlyInsurance,
      monthlyHoa,
      monthlyPmi,
      totalClosingCosts,
      earnestMoneyDeposit,
      configuration
    });
  }, [loanAmount, selectedLoanType, baseRate, purchasePrice, downPayment, monthlyTaxes, customMonthlyInsurance, monthlyHoa, monthlyPmi, totalClosingCosts, earnestMoneyDeposit, configuration]);

  // Handler for insurance selection from Coventry lookup
  const handleInsuranceSelect = useCallback((annualPremium: number) => {
    const monthlyValue = Math.round(annualPremium / 12);
    setCustomMonthlyInsurance(monthlyValue);
    onInsuranceChange?.(monthlyValue);
  }, [onInsuranceChange]);

  // Notify parent when scenarios are calculated
  useEffect(() => {
    if (onScenariosCalculated && scenarios.length > 0) {
      onScenariosCalculated(scenarios);
    }
  }, [scenarios, onScenariosCalculated]);

  // Calculate value score for each scenario
  const calculateValueScore = (scenario: BuydownScenario): number => {
    // Net benefit = savings minus extra cash needed
    const netBenefit = (scenario.totalSavings || 0) - scenario.extraCashNeeded;
    
    // Lower cash to close is better (normalize to 0-1 scale)
    const cashToCloseScore = 1 / (1 + scenario.cashToClose / 10000);
    
    // Shorter break-even is better (normalize to 0-1 scale)
    const breakEvenScore = scenario.breakEvenMonths && scenario.breakEvenMonths > 0
      ? 1 / (1 + scenario.breakEvenMonths / 12)
      : 0;
    
    // Weighted scoring formula
    return (
      netBenefit * 0.5 +          // 50% weight on net benefit
      cashToCloseScore * 25000 +  // 25% weight on cash to close
      breakEvenScore * 10000      // 25% weight on break-even speed
    );
  };

  // Calculate value scores for all scenarios and find the best one
  const scenariosWithScores = useMemo(() => {
    return scenarios.map(scenario => ({
      ...scenario,
      valueScore: calculateValueScore(scenario)
    }));
  }, [scenarios]);

  // Find the scenario with highest value score
  const bestScenario = useMemo(() => {
    return scenariosWithScores.reduce((best, current) => 
      current.valueScore > best.valueScore ? current : best
    );
  }, [scenariosWithScores]);

  const renderScenarioCard = (scenario: BuydownScenario) => {
    const isRecommended = scenario.type === bestScenario.type;
    const netBenefit = (scenario.totalSavings || 0) - scenario.extraCashNeeded;
    const basePointsWithoutInvestor = configuration.investorAdditionalPoints 
      ? configuration.fixedPoints - configuration.investorAdditionalPoints 
      : configuration.fixedPoints;
    
    return (
      <Card className={`p-6 space-y-4 ${isRecommended ? 'border-2 border-primary' : ''}`}>
        {isRecommended && (
          <div className="flex items-center gap-2 text-primary font-semibold text-sm">
            <Check className="w-4 h-4" />
            <span>BEST VALUE</span>
          </div>
        )}
        
        <div className="space-y-2">
          <h3 className="text-xl font-bold">{scenario.name}</h3>
          <p className="text-sm text-muted-foreground">{scenario.description}</p>
        </div>

        {/* Monthly Payment Timeline */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">Monthly Payment Timeline</Label>
          <div className="space-y-1">
            {scenario.paymentSchedule.map((payment, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <span className="text-muted-foreground">{payment.period}</span>
                <span className="font-mono font-semibold">{formatCurrency(payment.monthlyPayment)}</span>
              </div>
            ))}
          </div>

          {/* PITI Breakdown for first period */}
          {scenario.paymentSchedule.length > 0 && (
            <Collapsible className="mt-3 pt-3 border-t border-dashed">
              <CollapsibleTrigger className="flex items-center justify-between w-full text-xs font-medium text-muted-foreground uppercase tracking-wide hover:text-foreground transition-colors group">
                <span>Payment Breakdown ({scenario.paymentSchedule[0].period})</span>
                <ChevronDown className="h-3.5 w-3.5 transition-transform duration-200 group-data-[state=open]:rotate-180" />
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-2">
                <PITIDonutChart
                  principalInterest={scenario.paymentSchedule[0].principalAndInterest}
                  taxes={scenario.paymentSchedule[0].propertyTaxes}
                  insurance={scenario.paymentSchedule[0].homeInsurance}
                  hoa={scenario.paymentSchedule[0].hoa}
                  pmi={scenario.paymentSchedule[0].pmi}
                  totalPayment={scenario.paymentSchedule[0].monthlyPayment}
                />
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>

        {/* Cost Breakdown */}
        <div className="pt-4 border-t space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm">Builder Credit</Label>
            <span className="font-semibold text-green-600">
              {formatCurrency(scenario.builderCredit)} ({scenario.builderCreditPercent}%)
            </span>
          </div>
          
          <div className="flex items-center justify-between">
            <Label className="text-sm">Points Cost to Builder</Label>
            {configuration.occupancyType !== 'primary' && configuration.investorAdditionalPoints ? (
              <div className="text-right">
                <div className="text-xs text-muted-foreground space-y-0.5">
                  <div>Base: {basePointsWithoutInvestor.toFixed(3)} pts</div>
                  <div className="text-amber-600 dark:text-amber-400">
                    Investor: +{configuration.investorAdditionalPoints} pts
                  </div>
                </div>
                <div className="font-mono text-sm font-semibold mt-1">
                  Total: {formatPoints(scenario.points)} pts = {formatCurrency(scenario.pointsCost)}
                </div>
              </div>
            ) : (
              <span className="font-mono text-sm">
                {formatPoints(scenario.points)} pts = {formatCurrency(scenario.pointsCost)}
              </span>
            )}
          </div>

          <div className="space-y-2 border-t pt-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Before Builder Credit:</span>
              <span className="font-mono text-muted-foreground">{formatCurrency(scenario.cashToCloseBeforeCredit)}</span>
            </div>
            {scenario.builderCredit > 0 && (
              <div className="flex items-center justify-between text-sm text-green-600 dark:text-green-400">
                <span>Builder Credit Applied:</span>
                <span className="font-mono">-{formatCurrency(scenario.builderCredit)}</span>
              </div>
            )}
            {scenario.earnestMoneyDeposit > 0 && (
              <div className="flex items-center justify-between text-sm text-green-600 dark:text-green-400">
                <span>Earnest Money Deposit:</span>
                <span className="font-mono">-{formatCurrency(scenario.earnestMoneyDeposit)}</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <Label className="font-bold">Cash to Close</Label>
              <span className="text-xl font-bold">{formatCurrency(scenario.cashToClose)}</span>
            </div>
          </div>
        </div>

        {/* ROI Analysis */}
        {scenario.breakEvenMonths && scenario.breakEvenMonths > 0 && (
          <div className="pt-4 border-t bg-muted/20 -mx-6 -mb-6 p-4 rounded-b-lg">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-primary">
                <TrendingDown className="w-4 h-4" />
                <Label className="font-semibold">ROI Analysis</Label>
              </div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Break-even period:</span>
                  <span className="font-semibold">{scenario.breakEvenMonths} months</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total 2-year savings:</span>
                  <span className="font-semibold text-green-600">
                    {formatCurrency(scenario.totalSavings || 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Net benefit (24 mo):</span>
                  <span className="font-bold text-green-600">
                    {formatCurrency(netBenefit)}
                  </span>
                </div>
              </div>
              
              {/* Why this is best value */}
              {isRecommended && (
                <div className="mt-3 pt-3 border-t border-primary/20">
                  <p className="text-xs font-semibold text-primary mb-1.5">Why this is best value:</p>
                  <ul className="list-disc list-inside space-y-1 text-xs text-muted-foreground">
                    {netBenefit > 0 && (
                      <li>Highest net benefit: {formatCurrency(netBenefit)} over 2 years</li>
                    )}
                    {scenario.breakEvenMonths && scenario.breakEvenMonths <= 12 && (
                      <li>Quick break-even: {scenario.breakEvenMonths} months</li>
                    )}
                    {scenario.cashToClose <= Math.min(...scenarios.map(s => s.cashToClose)) && (
                      <li>Lowest cash to close required</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <DollarSign className="w-6 h-6" />
          Builder Incentive Calculator
        </h2>
        <p className="text-muted-foreground">
          Compare rate buydown options and builder incentives to maximize your savings
        </p>
      </div>

      {/* Loan Type and Occupancy Selectors */}
      <Card className="p-4 space-y-6">
        <LoanTypeSelector 
          value={selectedLoanType} 
          onChange={handleLoanTypeChange}
          basePoints={currentBasePoints}
        />
        
        {selectedLoanType === 'conventional' && (
          <OccupancyTypeSelector
            value={occupancyType}
            onChange={setOccupancyType}
            investorAdditionalPoints={configuration.investorAdditionalPoints || 0}
          />
        )}
      </Card>

      {/* Basic Configuration */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="builderName">Builder Name</Label>
            <Input
              id="builderName"
              value={builderName}
              onChange={(e) => setBuilderName(e.target.value)}
              placeholder="Enter builder name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lenderName">Lender Name</Label>
            <Input
              id="lenderName"
              value={lenderName}
              onChange={(e) => setLenderName(e.target.value)}
              placeholder="Enter lender name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="purchasePrice">Purchase Price</Label>
            <Input
              id="purchasePrice"
              type="text"
              value={displayPurchasePrice}
              onChange={(e) => {
                const inputValue = e.target.value;
                const formatted = formatCurrencyInput(inputValue);
                setDisplayPurchasePrice(formatted);
                
                const cleaned = parseCurrencyInput(inputValue);
                const numericValue = cleaned ? Number(cleaned) : 0;
                setPurchasePrice(numericValue);
              }}
              placeholder="$0"
            />
          </div>
        </div>
      </Card>

      {/* Coventry Insurance Lookup */}
      {builderName.toLowerCase().includes('coventry') && (
        <Card className="p-4">
          <CoventryInsuranceSelector
            selectedCommunity={selectedCommunity}
            selectedModel={selectedModel}
            onCommunityChange={setSelectedCommunity}
            onModelChange={setSelectedModel}
            onInsuranceSelect={handleInsuranceSelect}
          />
          {customMonthlyInsurance !== monthlyInsurance && (
            <div className="mt-4 pt-4 border-t flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Using Pine Ridge rate: {formatCurrency(customMonthlyInsurance)}/mo
              </span>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => {
                  setCustomMonthlyInsurance(monthlyInsurance);
                  onInsuranceChange?.(monthlyInsurance);
                }}
              >
                Reset to default
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* Advanced Rate & Points Configuration */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            <h3 className="text-lg font-semibold">Rate & Points Configuration</h3>
          </div>
          <Button variant="outline" size="sm" onClick={handleResetToDefaults}>
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset to Defaults
          </Button>
        </div>
        
        <Accordion type="multiple" className="w-full">
          {/* Fixed Rate Configuration */}
          <AccordionItem value="fixed">
            <AccordionTrigger>Fixed Rate Configuration</AccordionTrigger>
            <AccordionContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4">
                <div className="space-y-2">
                  <Label htmlFor="fixedRate">Fixed Rate (%)</Label>
                  <Input
                    id="fixedRate"
                    type="number"
                    step="0.001"
                    value={configuration.fixedRate}
                    onChange={(e) => updateConfiguration({ fixedRate: Number(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fixedPoints">Points Cost</Label>
                  <Input
                    id="fixedPoints"
                    type="number"
                    step="0.001"
                    value={configuration.fixedPoints}
                    onChange={(e) => updateConfiguration({ fixedPoints: Number(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fixedBuilderCredit">Builder Credit (%)</Label>
                  <Input
                    id="fixedBuilderCredit"
                    type="number"
                    step="0.1"
                    value={configuration.fixedBuilderCreditPercent}
                    onChange={(e) => updateConfiguration({ fixedBuilderCreditPercent: Number(e.target.value) })}
                  />
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* 1/0 Buydown Configuration */}
          <AccordionItem value="1-0">
            <AccordionTrigger>1/0 Buydown Configuration</AccordionTrigger>
            <AccordionContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
                <div className="space-y-2">
                  <Label htmlFor="oneZeroYear1Rate">Year 1 Rate (%)</Label>
                  <Input
                    id="oneZeroYear1Rate"
                    type="number"
                    step="0.001"
                    value={configuration.oneZeroYear1Rate}
                    onChange={(e) => updateConfiguration({ oneZeroYear1Rate: Number(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="oneZeroYear2PlusRate">Years 2-30 Rate (%)</Label>
                  <Input
                    id="oneZeroYear2PlusRate"
                    type="number"
                    step="0.001"
                    value={configuration.oneZeroYear2PlusRate}
                    onChange={(e) => updateConfiguration({ oneZeroYear2PlusRate: Number(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="oneZeroPoints">Points Cost</Label>
                  <Input
                    id="oneZeroPoints"
                    type="number"
                    step="0.001"
                    value={configuration.oneZeroPoints}
                    onChange={(e) => updateConfiguration({ oneZeroPoints: Number(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="oneZeroBuilderCredit">Builder Credit (%)</Label>
                  <Input
                    id="oneZeroBuilderCredit"
                    type="number"
                    step="0.1"
                    value={configuration.oneZeroBuilderCreditPercent}
                    onChange={(e) => updateConfiguration({ oneZeroBuilderCreditPercent: Number(e.target.value) })}
                  />
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* 2/1 Buydown Configuration */}
          <AccordionItem value="2-1">
            <AccordionTrigger>2/1 Buydown Configuration</AccordionTrigger>
            <AccordionContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
                <div className="space-y-2">
                  <Label htmlFor="twoOneYear1Rate">Year 1 Rate (%)</Label>
                  <Input
                    id="twoOneYear1Rate"
                    type="number"
                    step="0.001"
                    value={configuration.twoOneYear1Rate}
                    onChange={(e) => updateConfiguration({ twoOneYear1Rate: Number(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="twoOneYear2Rate">Year 2 Rate (%)</Label>
                  <Input
                    id="twoOneYear2Rate"
                    type="number"
                    step="0.001"
                    value={configuration.twoOneYear2Rate}
                    onChange={(e) => updateConfiguration({ twoOneYear2Rate: Number(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="twoOneYear3PlusRate">Years 3-30 Rate (%)</Label>
                  <Input
                    id="twoOneYear3PlusRate"
                    type="number"
                    step="0.001"
                    value={configuration.twoOneYear3PlusRate}
                    onChange={(e) => updateConfiguration({ twoOneYear3PlusRate: Number(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="twoOnePoints">Points Cost</Label>
                  <Input
                    id="twoOnePoints"
                    type="number"
                    step="0.001"
                    value={configuration.twoOnePoints}
                    onChange={(e) => updateConfiguration({ twoOnePoints: Number(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="twoOneBuilderCredit">Builder Credit (%)</Label>
                  <Input
                    id="twoOneBuilderCredit"
                    type="number"
                    step="0.1"
                    value={configuration.twoOneBuilderCreditPercent}
                    onChange={(e) => updateConfiguration({ twoOneBuilderCreditPercent: Number(e.target.value) })}
                  />
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* 3/2/1 Buydown Configuration */}
          <AccordionItem value="3-2-1">
            <AccordionTrigger>3/2/1 Buydown Configuration</AccordionTrigger>
            <AccordionContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
                <div className="space-y-2">
                  <Label htmlFor="threeTwoOneYear1Rate">Year 1 Rate (%)</Label>
                  <Input
                    id="threeTwoOneYear1Rate"
                    type="number"
                    step="0.001"
                    value={configuration.threeTwoOneYear1Rate}
                    onChange={(e) => updateConfiguration({ threeTwoOneYear1Rate: Number(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="threeTwoOneYear2Rate">Year 2 Rate (%)</Label>
                  <Input
                    id="threeTwoOneYear2Rate"
                    type="number"
                    step="0.001"
                    value={configuration.threeTwoOneYear2Rate}
                    onChange={(e) => updateConfiguration({ threeTwoOneYear2Rate: Number(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="threeTwoOneYear3Rate">Year 3 Rate (%)</Label>
                  <Input
                    id="threeTwoOneYear3Rate"
                    type="number"
                    step="0.001"
                    value={configuration.threeTwoOneYear3Rate}
                    onChange={(e) => updateConfiguration({ threeTwoOneYear3Rate: Number(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="threeTwoOneYear4PlusRate">Years 4-30 Rate (%)</Label>
                  <Input
                    id="threeTwoOneYear4PlusRate"
                    type="number"
                    step="0.001"
                    value={configuration.threeTwoOneYear4PlusRate}
                    onChange={(e) => updateConfiguration({ threeTwoOneYear4PlusRate: Number(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="threeTwoOnePoints">Points Cost</Label>
                  <Input
                    id="threeTwoOnePoints"
                    type="number"
                    step="0.001"
                    value={configuration.threeTwoOnePoints}
                    onChange={(e) => updateConfiguration({ threeTwoOnePoints: Number(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="threeTwoOneBuilderCredit">Builder Credit (%)</Label>
                  <Input
                    id="threeTwoOneBuilderCredit"
                    type="number"
                    step="0.1"
                    value={configuration.threeTwoOneBuilderCreditPercent}
                    onChange={(e) => updateConfiguration({ threeTwoOneBuilderCreditPercent: Number(e.target.value) })}
                  />
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </Card>

      {/* Scenarios Grid */}
      <Tabs defaultValue="grid" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="grid">Grid View</TabsTrigger>
          <TabsTrigger value="compare">Side-by-Side</TabsTrigger>
          <TabsTrigger value="ratesheet">Rate Sheet</TabsTrigger>
          <TabsTrigger value="breakeven">Breakeven</TabsTrigger>
        </TabsList>

        <TabsContent value="grid" className="space-y-4 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {scenarios.map((scenario) => (
              <div key={scenario.type}>
                {renderScenarioCard(scenario)}
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="compare" className="mt-6">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 font-semibold">Feature</th>
                  {scenarios.map((scenario) => (
                    <th key={scenario.type} className="text-center p-3 font-semibold">
                      {scenario.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="border-b bg-muted/20">
                  <td className="p-3 font-medium">Year 1 Payment</td>
                  {scenarios.map((scenario) => (
                    <td key={scenario.type} className="text-center p-3 font-mono">
                      {formatCurrency(scenario.paymentSchedule[0].monthlyPayment)}
                    </td>
                  ))}
                </tr>
                <tr className="border-b">
                  <td className="p-3 font-medium">Builder Credit</td>
                  {scenarios.map((scenario) => (
                    <td key={scenario.type} className="text-center p-3 text-green-600 font-semibold">
                      {formatCurrency(scenario.builderCredit)}
                    </td>
                  ))}
                </tr>
                <tr className="border-b bg-muted/20">
                  <td className="p-3 font-medium">Points Cost</td>
                  {scenarios.map((scenario) => (
                    <td key={scenario.type} className="text-center p-3 font-mono text-sm">
                      {formatPoints(scenario.points)} pts
                    </td>
                  ))}
                </tr>
                <tr className="border-b">
                  <td className="p-3 font-medium">Cash to Close</td>
                  {scenarios.map((scenario) => (
                    <td key={scenario.type} className="text-center p-3 font-bold">
                      {formatCurrency(scenario.cashToClose)}
                    </td>
                  ))}
                </tr>
                <tr className="border-b bg-muted/20">
                  <td className="p-3 font-medium">Break-even</td>
                  {scenarios.map((scenario) => (
                    <td key={scenario.type} className="text-center p-3">
                      {scenario.breakEvenMonths ? `${scenario.breakEvenMonths} mo` : 'N/A'}
                    </td>
                  ))}
                </tr>
                <tr className="border-b">
                  <td className="p-3 font-medium">2-Year Savings</td>
                  {scenarios.map((scenario) => (
                    <td key={scenario.type} className="text-center p-3 font-semibold text-green-600">
                      {scenario.totalSavings ? formatCurrency(scenario.totalSavings) : 'N/A'}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="ratesheet" className="mt-6">
          <RateSheetComparison
            loanAmount={loanAmount}
            purchasePrice={purchasePrice}
            baseRate={baseRate}
            monthlyTaxes={monthlyTaxes}
            monthlyInsurance={monthlyInsurance}
            totalClosingCosts={totalClosingCosts}
            earnestMoneyDeposit={earnestMoneyDeposit}
          />
        </TabsContent>

        <TabsContent value="breakeven" className="mt-6">
          <BreakevenAnalysisChart scenarios={scenarios} />
        </TabsContent>
      </Tabs>

      {/* Info Footer */}
      <Card className="p-4 bg-muted/20">
        <div className="flex items-start gap-3">
          <Calendar className="w-5 h-5 text-muted-foreground mt-0.5" />
          <div className="space-y-1 text-sm">
            <p className="font-medium">How Builder Incentives Work</p>
            <p className="text-muted-foreground">
              The builder ({builderName}) pays for rate buydown "points" to temporarily lower your interest rate. 
              Your incentive percentage (2%, 1%, or 0%) is based on which buydown option you choose, 
              reducing your out-of-pocket closing costs accordingly.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default BuydownIncentiveCalculator;
